import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  Suspense,
} from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  FaHome,
  FaCalendarAlt,
  FaSync,
  FaSearch,
  FaTimes,
  FaGavel,
  FaEdit,
  FaTrashAlt,
  FaInfoCircle,
  FaRupeeSign,
  FaExchangeAlt,
} from "react-icons/fa";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";

/* Modal is only mounted on demand — lazy-load. */
const MdlSelectSession = React.lazy(() =>
  import("../dialog/MdlSelectSession")
);

const fontStack =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/** Page-local design tokens (mirrors FineSettings/PayRoll family). */
const ui = {
  pageBg: "#f5f7fb",
  surface: "#ffffff",
  surfaceAlt: "#f8fafc",
  border: "rgba(148, 163, 184, 0.32)",
  divider: "rgba(148, 163, 184, 0.22)",
  muted: "#64748b",
  text: "#0f172a",
  textSoft: "#334155",
  ring: "rgba(3, 169, 244, 0.18)",
  danger: "#ef4444",
  success: "#10b981",
  warn: "#f59e0b",
  info: "#0ea5e9",
  radiusXl: 20,
  radiusLg: 16,
  radiusMd: 12,
  shadowSoft:
    "0 1px 2px rgba(15, 23, 42, 0.04), 0 2px 8px rgba(15, 23, 42, 0.04)",
  shadowCard:
    "0 1px 2px rgba(15, 23, 42, 0.04), 0 6px 24px rgba(15, 23, 42, 0.05)",
  shadowLift:
    "0 1px 2px rgba(15, 23, 42, 0.05), 0 16px 48px rgba(15, 23, 42, 0.10)",
  heroGrad: (c) =>
    `linear-gradient(120deg, ${c.primary_color_shade9} 0%, ${c.primary_color_shade7} 45%, ${c.primary_color_shade5} 100%)`,
};

const STATUS_PALETTE = {
  active: {
    bg: "rgba(16, 185, 129, 0.14)",
    color: "#047857",
    dot: "#10b981",
  },
  inactive: {
    bg: "rgba(239, 68, 68, 0.14)",
    color: "#b91c1c",
    dot: "#ef4444",
  },
};

const CHIP_PALETTES = {
  type:  { bg: "rgba(3, 169, 244, 0.10)",  color: "#0369a1" },
  grace: { bg: "rgba(16, 185, 129, 0.12)", color: "#047857" },
  base:  { bg: "rgba(139, 92, 246, 0.14)", color: "#6d28d9" },
  max:   { bg: "rgba(245, 158, 11, 0.16)", color: "#b45309" },
};

/** Normalize a server row to consistent JS types. */
function normalizeRule(raw) {
  const statusRaw = raw?.status;
  let isActive;
  if (typeof statusRaw === "string") {
    isActive =
      statusRaw === "1" ||
      statusRaw.toLowerCase() === "active";
  } else {
    isActive = !!statusRaw;
  }
  return {
    ...raw,
    fine_rule_id: raw?.fine_rule_id ?? "",
    name: raw?.name ?? "",
    rule_type: raw?.rule_type ?? "",
    base_value: raw?.base_value ?? "",
    grace_days: raw?.grace_days ?? "",
    max_cap: raw?.max_cap ?? "",
    description: raw?.description ?? "",
    rule_values: raw?.rule_values ?? "",
    _isActive: isActive,
    status: isActive ? "Active" : "Inactive",
  };
}

function formatINR(value) {
  if (value === null || value === undefined || value === "") return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return String(value);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numeric);
}

/** Clip a server date string to YYYY-MM-DD (HTML date-input format). */
function toIsoDate(value) {
  if (value == null) return "";
  const s = String(value).trim();
  if (!s) return "";
  return s.length >= 10 ? s.substring(0, 10) : s;
}

/* Scoped page-level CSS. */
const PAGE_CSS = `
.frule-page :is(.form-control, .form-select, input, textarea, select) {
  border-radius: 10px;
  border: 1px solid ${ui.border};
  background: ${ui.surface};
  padding: 9px 12px;
  font-size: 13px;
  font-weight: 600;
  color: ${ui.text};
  transition: border-color .15s ease, box-shadow .15s ease;
}
.frule-page :is(.form-control, .form-select, input, textarea, select):focus {
  border-color: ${COLORS.primary_color_shade5};
  box-shadow: 0 0 0 4px ${ui.ring};
  outline: none;
}
.frule-page .frule-search-wrap { position: relative; display: inline-flex; align-items: center; }
.frule-page .frule-search-wrap .frule-search-icon {
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  color: rgba(255,255,255,0.75); font-size: 12px; pointer-events: none;
}
.frule-page .frule-search-wrap .frule-search-clear {
  position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
  background: rgba(255,255,255,0.18); border: 0;
  width: 20px; height: 20px; border-radius: 999px;
  color: #fff; font-size: 10px;
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
}
.frule-page .frule-search {
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.32);
  color: #fff;
  border-radius: 999px;
  padding: 7px 28px 7px 30px;
  font-size: 13px;
  font-weight: 500;
  outline: none;
  min-width: 220px;
  backdrop-filter: blur(6px);
}
.frule-page .frule-search::placeholder { color: rgba(255,255,255,0.72); }
.frule-page .frule-search:focus {
  background: rgba(255,255,255,0.26);
  border-color: rgba(255,255,255,0.55);
  box-shadow: 0 0 0 4px rgba(255,255,255,0.12);
}
.frule-page .frule-icon-btn {
  width: 34px; height: 34px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 999px;
  background: rgba(255,255,255,0.18);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.32);
  backdrop-filter: blur(6px);
  cursor: pointer;
  transition: background .15s ease;
}
.frule-page .frule-icon-btn:hover:not(:disabled) { background: rgba(255,255,255,0.28); }
.frule-page .frule-icon-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.frule-page .frule-icon-btn.spinning svg { animation: frule-spin 1s linear infinite; }
@keyframes frule-spin { 100% { transform: rotate(360deg); } }
.frule-page .frule-skeleton {
  position: relative; overflow: hidden;
  background: rgba(148, 163, 184, 0.18);
  border-radius: 8px;
}
.frule-page .frule-skeleton::after {
  content: ""; position: absolute; inset: 0; transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent);
  animation: frule-shimmer 1.2s infinite;
}
@keyframes frule-shimmer { 100% { transform: translateX(100%); } }
/* Escape global ".btn { background-image: none !important; box-shadow: none !important; }". */
.frule-page .frule-cta {
  background-image: linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 100%) !important;
  background-color: ${COLORS.primary_color_shade5} !important;
  box-shadow: 0 6px 18px rgba(3, 169, 244, 0.35) !important;
  color: #fff !important;
  border: 1px solid ${COLORS.primary_color_shade7} !important;
}
.frule-page .frule-cta:hover { filter: brightness(1.04); }
.frule-page .frule-pill {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.32);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  backdrop-filter: blur(6px);
  transition: background .15s ease;
  white-space: nowrap;
}
.frule-page .frule-pill:hover:not(:disabled) { background: rgba(255,255,255,0.28); }
.frule-page .frule-pill:disabled { opacity: 0.55; cursor: not-allowed; }

/* List-row design */
.frule-page .frule-list-row {
  background: ${ui.surface};
  border: 1px solid ${ui.border};
  border-radius: ${ui.radiusMd}px;
  box-shadow: ${ui.shadowSoft};
  padding: 14px 18px;
  display: grid;
  grid-template-columns: minmax(170px, 0.9fr) minmax(0, 3fr) auto;
  gap: 16px;
  align-items: center;
  min-height: 76px;
}
@media (max-width: 991.98px) {
  .frule-page .frule-list-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  .frule-page .frule-list-row-actions { justify-content: flex-start; }
}
.frule-page .frule-list-row-title {
  display: flex; align-items: center; gap: 12px; min-width: 0;
}
.frule-page .frule-list-row-icon {
  width: 38px; height: 38px; border-radius: 11px;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(3, 169, 244, 0.14);
  color: ${COLORS.primary_color_shade8};
  flex-shrink: 0;
  font-size: 15px;
}
.frule-page .frule-list-row-name {
  font-weight: 800; font-size: 15.5px; color: ${ui.text};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  letter-spacing: 0.005em;
}
.frule-page .frule-list-row-meta {
  font-size: 12.5px; color: ${ui.textSoft}; font-weight: 600;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  margin-top: 1px;
}
.frule-page .frule-chip-stats {
  display: flex; flex-wrap: wrap; gap: 7px; min-width: 0;
}
.frule-page .frule-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 11px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.4;
  white-space: nowrap;
}
.frule-page .frule-chip-label { font-weight: 700; opacity: 0.85; }
.frule-page .frule-chip-value { font-weight: 800; }
.frule-page .frule-list-row-actions {
  display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap;
  justify-content: flex-end;
}
.frule-page .frule-status-chip {
  display: inline-flex; align-items: center; gap: 5px;
  font-weight: 800; font-size: 11px;
  padding: 5px 10px; border-radius: 999px;
  letter-spacing: 0.06em; text-transform: uppercase;
}
.frule-page .frule-icon-action {
  width: 36px; height: 36px;
  display: inline-flex; align-items: center; justify-content: center;
  border: 0;
  border-radius: 11px;
  font-size: 13.5px;
  cursor: pointer;
  transition: transform .15s ease, filter .15s ease, box-shadow .15s ease;
}
.frule-page .frule-icon-action:hover:not(:disabled) {
  transform: translateY(-1px); filter: brightness(1.04);
}
.frule-page .frule-icon-action:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }

.frule-page .lift { transition: transform .15s ease, box-shadow .15s ease; }
.frule-page .lift:hover { transform: translateY(-2px); box-shadow: ${ui.shadowLift}; }
.frule-page @keyframes frule-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.55; } }
.frule-page .frule-dot-pulse { animation: frule-pulse 2s ease-in-out infinite; }

/* Dialog (works both inside the page tree and when portaled to body via the
   .frule-portal wrapper) */
.frule-page .frule-modal-backdrop,
.frule-portal .frule-modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  z-index: 1080;
  padding: 16px;
  animation: frule-fade-in .15s ease-out;
}
@keyframes frule-fade-in { from { opacity: 0; } to { opacity: 1; } }
.frule-page .frule-modal,
.frule-portal .frule-modal {
  width: 100%;
  max-width: 680px;
  background: ${ui.surface};
  border-radius: ${ui.radiusLg}px;
  border: 1px solid ${ui.border};
  box-shadow: ${ui.shadowLift};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 32px);
  animation: frule-rise .18s ease-out;
}
@keyframes frule-rise {
  from { transform: translateY(8px) scale(0.99); opacity: 0; }
  to { transform: translateY(0) scale(1); opacity: 1; }
}
.frule-page .frule-modal-header,
.frule-portal .frule-modal-header {
  padding: 14px 18px;
  color: #fff;
  background: linear-gradient(135deg, ${COLORS.primary_color_shade8} 0%, ${COLORS.primary_color_shade6} 100%);
  position: relative;
  flex-shrink: 0;
}
.frule-page .frule-modal-close,
.frule-portal .frule-modal-close {
  position: absolute; top: 12px; right: 12px;
  width: 28px; height: 28px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 999px;
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.32);
  color: #fff; cursor: pointer;
  backdrop-filter: blur(6px);
  font-size: 11px;
}
.frule-page .frule-modal-close:hover,
.frule-portal .frule-modal-close:hover { background: rgba(255,255,255,0.28); }
.frule-page .frule-modal-body,
.frule-portal .frule-modal-body { padding: 18px; overflow: auto; flex: 1 1 auto; min-height: 0; }
.frule-page .frule-modal-footer,
.frule-portal .frule-modal-footer {
  padding: 12px 18px;
  border-top: 1px solid ${ui.divider};
  display: flex; justify-content: flex-end; gap: 8px;
  background: ${ui.surfaceAlt};
  flex-shrink: 0;
}
.frule-page .frule-modal-label {
  font-size: 11px;
  font-weight: 800;
  color: ${ui.muted};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.frule-page .frule-btn-ghost {
  background: ${ui.surface};
  border: 1px solid ${ui.border};
  color: ${ui.textSoft};
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.frule-page .frule-btn-ghost:hover:not(:disabled) { background: ${ui.surfaceAlt}; }
.frule-page .frule-btn-ghost:disabled { opacity: 0.55; cursor: not-allowed; }
`;

/* Decorative orbs (constants — never re-rendered). */
const HERO_ORB_TR = (
  <span
    aria-hidden
    style={{
      position: "absolute",
      right: -60,
      top: -60,
      width: 220,
      height: 220,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.08)",
      filter: "blur(2px)",
    }}
  />
);
const HERO_ORB_BL = (
  <span
    aria-hidden
    style={{
      position: "absolute",
      left: -40,
      bottom: -80,
      width: 180,
      height: 180,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.06)",
    }}
  />
);

/* ---------- Sub-components ---------- */

function DialogShell({ title, subtitle, onClose, footer, children, maxWidth }) {
  /* Close on Escape. */
  useEffect(() => {
    if (typeof onClose !== "function") return undefined;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && typeof onClose === "function") {
      onClose();
    }
  };

  /* Render via portal so ancestor transforms/filters cannot trap the fixed
     backdrop. The wrapper carries both `frule-page` (for existing scoped CSS)
     and `frule-portal` (so we can add portal-only tweaks). */
  return createPortal(
    <div className="frule-page frule-portal">
      <style>{PAGE_CSS}</style>
      <div
        className="frule-modal-backdrop"
        role="dialog"
        aria-modal="true"
        onClick={handleBackdropClick}
      >
        <div
          className="frule-modal"
          style={maxWidth ? { maxWidth } : undefined}
        >
          <div className="frule-modal-header">
            {onClose ? (
              <button
                type="button"
                className="frule-modal-close"
                onClick={onClose}
                aria-label="Close"
                title="Close (Esc)"
              >
                <FaTimes aria-hidden />
              </button>
            ) : null}
            <div style={{ fontWeight: 800, fontSize: 16, paddingRight: 32 }}>
              {title}
            </div>
            {subtitle ? (
              <div
                style={{
                  fontSize: 12,
                  opacity: 0.9,
                  marginTop: 2,
                  paddingRight: 32,
                }}
              >
                {subtitle}
              </div>
            ) : null}
          </div>
          <div className="frule-modal-body">{children}</div>
          {footer ? <div className="frule-modal-footer">{footer}</div> : null}
        </div>
      </div>
    </div>,
    document.body
  );
}

function SkeletonCard() {
  return (
    <div
      className="frule-list-row"
      style={{ marginBottom: 8, borderLeft: `4px solid ${ui.divider}` }}
    >
      <div className="frule-list-row-title">
        <div
          className="frule-skeleton"
          style={{ width: 38, height: 38, borderRadius: 11 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            className="frule-skeleton"
            style={{ height: 12, width: "70%", marginBottom: 6 }}
          />
          <div className="frule-skeleton" style={{ height: 10, width: "50%" }} />
        </div>
      </div>
      <div className="frule-chip-stats">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="frule-skeleton"
            style={{ height: 22, width: 78, borderRadius: 999 }}
          />
        ))}
      </div>
      <div className="frule-list-row-actions">
        <div
          className="frule-skeleton"
          style={{ height: 22, width: 60, borderRadius: 999 }}
        />
        <div
          className="frule-skeleton"
          style={{ height: 36, width: 36, borderRadius: 11 }}
        />
        <div
          className="frule-skeleton"
          style={{ height: 36, width: 36, borderRadius: 11 }}
        />
      </div>
    </div>
  );
}

const FineRuleCard = React.memo(function FineRuleCard({
  rule,
  onEdit,
  onDelete,
}) {
  const isActive = !!rule._isActive;
  const palette = isActive ? STATUS_PALETTE.active : STATUS_PALETTE.inactive;
  const ruleType = rule.rule_type || "—";

  const chips = [
    { label: "Type", value: ruleType, ...CHIP_PALETTES.type },
    {
      label: "Grace",
      value: `${rule.grace_days ?? 0}d`,
      ...CHIP_PALETTES.grace,
    },
    {
      label: "Base",
      value:
        rule.base_value === "" || rule.base_value == null
          ? "—"
          : formatINR(rule.base_value),
      ...CHIP_PALETTES.base,
    },
    {
      label: "Max Cap",
      value:
        rule.max_cap === "" || rule.max_cap == null || Number(rule.max_cap) === 0
          ? "No limit"
          : formatINR(rule.max_cap),
      ...CHIP_PALETTES.max,
    },
  ];

  return (
    <div
      className="frule-list-row lift"
      style={{ marginBottom: 8, borderLeft: `4px solid ${palette.dot}` }}
    >
      <div className="frule-list-row-title">
        <span className="frule-list-row-icon" aria-hidden>
          <FaGavel />
        </span>
        <div className="min-w-0" style={{ flex: 1 }}>
          <div
            className="frule-list-row-name"
            title={rule.name || rule.fine_rule_id || "Unnamed rule"}
          >
            {rule.name || rule.fine_rule_id || "Unnamed rule"}
          </div>
          <div className="frule-list-row-meta">
            ID: {rule.fine_rule_id || "—"}
            {rule.description ? (
              <>
                {" · "}
                <span title={rule.description}>{rule.description}</span>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <div className="frule-chip-stats" aria-label="Fine rule stats">
        {chips.map((c) => (
          <span
            key={c.label}
            className="frule-chip"
            style={{ background: c.bg, color: c.color }}
            title={`${c.label}: ${c.value}`}
          >
            <span className="frule-chip-label">{c.label}</span>
            <span className="frule-chip-value">{c.value}</span>
          </span>
        ))}
      </div>

      <div className="frule-list-row-actions">
        <span
          className="frule-status-chip"
          style={{ background: palette.bg, color: palette.color }}
        >
          <span
            aria-hidden
            className={isActive ? "frule-dot-pulse" : undefined}
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: palette.dot,
            }}
          />
          {isActive ? "Active" : "Inactive"}
        </span>
        <button
          type="button"
          className="frule-icon-action"
          onClick={() => onEdit(rule)}
          style={{ background: "rgba(14, 165, 233, 0.12)", color: "#0369a1" }}
          title="Edit fine rule"
          aria-label="Edit fine rule"
        >
          <FaEdit />
        </button>
        <button
          type="button"
          className="frule-icon-action"
          onClick={() => onDelete(rule)}
          style={{ background: "rgba(239, 68, 68, 0.12)", color: "#b91c1c" }}
          title="Delete fine rule"
          aria-label="Delete fine rule"
        >
          <FaTrashAlt />
        </button>
      </div>
    </div>
  );
});

/* ---------- Main component ---------- */

const FineRuleSettings = () => {
  const [SESSION_ID, setSESSION_ID] = useState("");
  const [SESSION_NAME, setSESSION_NAME] = useState("");
  const [SESSION_START_DATE, setSESSION_START_DATE] = useState("");
  const [SESSION_END_DATE, setSESSION_END_DATE] = useState("");
  const [isOpenSessionDialog, setIsOpenSessionDialog] = useState(false);

  const [fineRules, setFineRules] = useState([]);
  const [isLoadingRules, setIsLoadingRules] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  /* Edit dialog */
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editRule, setEditRule] = useState({
    fine_rule_id: "",
    name: "",
    rule_type: "",
    base_value: "",
    grace_days: "",
    max_cap: "",
    description: "",
    rule_values: "",
    status: "Active",
  });
  const [noMaxCapLimit, setNoMaxCapLimit] = useState(false);

  /* Stale-request guard. */
  const fetchReqIdRef = useRef(0);

  /* Mount: scroll top + bootstrap session. */
  useEffect(() => {
    window.scrollTo(0, 0);
    const session =
      SessionManager.getCurrentSession && SessionManager.getCurrentSession();
    if (Array.isArray(session) && session[0]) {
      setSESSION_ID(session[0].session_id || "");
      setSESSION_NAME(session[0].session_name || "");
      setSESSION_START_DATE(toIsoDate(session[0].start_date));
      setSESSION_END_DATE(toIsoDate(session[0].end_date));
    } else {
      setIsOpenSessionDialog(true);
    }
    return () => {
      fetchReqIdRef.current = -1;
    };
  }, []);

  const fetchFineRules = useCallback(
    async (silent = false, sessionIdOverride) => {
      const sid =
        sessionIdOverride !== undefined && sessionIdOverride !== null
          ? sessionIdOverride
          : SESSION_ID;
      if (!sid) {
        setFineRules([]);
        if (!silent) myToast.notify_warning("Please select a session first");
        return;
      }
      const reqId = ++fetchReqIdRef.current;
      setIsLoadingRules(true);
      try {
        const FUNCTION_NAME = "getFineRules.php";
        const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;
        const schoolCode =
          SessionManager.getSchoolCode && SessionManager.getSchoolCode();
        const params = { school_code: schoolCode, session_id: sid };

        const queryString = new URLSearchParams(
          Object.fromEntries(
            Object.entries(params).map(([k, v]) => [
              k,
              v != null ? String(v) : "",
            ])
          )
        ).toString();
        console.log(
          "getFineRules full URL:",
          `${URL}?${queryString}`
        );

        const { data: response } = await axios.get(URL, { params });
        if (reqId !== fetchReqIdRef.current) return;

        if (response?.api_status === "OK" && Array.isArray(response?.data)) {
          const normalized = response.data.map(normalizeRule);
          setFineRules(normalized);
          if (!silent) {
            myToast.notify_success(
              `Loaded ${normalized.length} fine rule${
                normalized.length === 1 ? "" : "s"
              }`
            );
          }
        } else {
          setFineRules([]);
          if (!silent) {
            myToast.notify_warning(
              response?.api_remark || "Failed to load fine rules"
            );
          }
        }
      } catch (e) {
        if (reqId !== fetchReqIdRef.current) return;
        setFineRules([]);
        if (!silent) {
          myToast.notify_error(
            "Error loading fine rules: " + (e?.message || "Unknown error")
          );
        }
      } finally {
        if (reqId === fetchReqIdRef.current) setIsLoadingRules(false);
      }
    },
    [SESSION_ID]
  );

  /* Auto-fetch when session changes. */
  useEffect(() => {
    if (SESSION_ID) fetchFineRules(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SESSION_ID]);

  const ChangeSession = useCallback(
    () => setIsOpenSessionDialog(true),
    []
  );
  const HandleCloseSessionDialog = useCallback(
    () => setIsOpenSessionDialog(false),
    []
  );
  const HandleSelectedSession = useCallback(
    (session_id, session_name, session_status, start_date, end_date) => {
      // Persist so the new session sticks across pages.
      try {
        SessionManager.saveCurrentSession &&
          SessionManager.saveCurrentSession([
            {
              session_id,
              session_name,
              status: session_status,
              start_date,
              end_date,
            },
          ]);
      } catch (_e) {
        /* non-fatal */
      }
      setSESSION_ID(session_id);
      setSESSION_NAME(session_name);
      setSESSION_START_DATE(toIsoDate(start_date));
      setSESSION_END_DATE(toIsoDate(end_date));
      setIsOpenSessionDialog(false);
      if (session_id) fetchFineRules(true, session_id);
    },
    [fetchFineRules]
  );

  const openEdit = useCallback((rule) => {
    const rawCap = rule.max_cap;
    const capStr =
      rawCap === null || rawCap === undefined ? "" : String(rawCap);
    const isNoLimit = capStr.trim() === "" || Number(capStr) === 0;
    setNoMaxCapLimit(isNoLimit);
    setEditRule({
      fine_rule_id: rule.fine_rule_id || "",
      name: rule.name || "",
      rule_type: rule.rule_type || "",
      base_value: rule.base_value ?? "",
      grace_days: rule.grace_days ?? "",
      max_cap: isNoLimit ? "" : capStr,
      description: rule.description || "",
      rule_values:
        rule.rule_values && typeof rule.rule_values === "object"
          ? JSON.stringify(rule.rule_values, null, 2)
          : rule.rule_values || "",
      status: rule._isActive ? "Active" : "Inactive",
    });
    setIsEditOpen(true);
  }, []);

  const closeEdit = useCallback(() => {
    if (editSaving) return;
    setIsEditOpen(false);
  }, [editSaving]);

  const saveEdit = useCallback(async () => {
    if (!editRule.fine_rule_id) {
      myToast.notify_warning("Missing fine_rule_id");
      return;
    }
    if (!editRule.name.trim()) {
      myToast.notify_warning("Rule name is required");
      return;
    }
    if (
      String(editRule.grace_days).trim() !== "" &&
      (Number.isNaN(Number(editRule.grace_days)) ||
        Number(editRule.grace_days) < 0)
    ) {
      myToast.notify_warning("Grace days must be a non-negative number");
      return;
    }
    if (
      String(editRule.base_value).trim() !== "" &&
      (Number.isNaN(Number(editRule.base_value)) ||
        Number(editRule.base_value) < 0)
    ) {
      myToast.notify_warning("Base value must be a non-negative number");
      return;
    }
    if (
      !noMaxCapLimit &&
      String(editRule.max_cap).trim() !== "" &&
      (Number.isNaN(Number(editRule.max_cap)) ||
        Number(editRule.max_cap) < 0)
    ) {
      myToast.notify_warning("Max cap must be a non-negative number");
      return;
    }
    // Validate JSON for rule_values when provided.
    if (editRule.rule_values && editRule.rule_values.trim()) {
      try {
        JSON.parse(editRule.rule_values);
      } catch (_) {
        myToast.notify_warning("Rule Values must be valid JSON");
        return;
      }
    }

    setEditSaving(true);
    try {
      const FUNCTION_NAME = "updateFineRule.php";
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;
      const schoolCode =
        SessionManager.getSchoolCode && SessionManager.getSchoolCode();
      const payload = {
        school_code: schoolCode,
        session_id: SESSION_ID,
        ...editRule,
        max_cap: noMaxCapLimit ? "No-Limit" : editRule.max_cap,
      };
      console.log("updateFineRule full URL:", `${URL}?…`, payload);
      const { data: response } = await axios.get(URL, { params: payload });
      if (response?.api_status === "OK") {
        myToast.notify_success("Fine rule updated successfully");
        setIsEditOpen(false);
        fetchFineRules(true);
      } else {
        myToast.notify_warning(response?.api_remark || "Update failed");
      }
    } catch (e) {
      console.error("Error updating fine rule:", e);
      myToast.notify_error(
        "Error updating fine rule: " + (e?.message || "Unknown error")
      );
    } finally {
      setEditSaving(false);
    }
  }, [editRule, noMaxCapLimit, SESSION_ID, fetchFineRules]);

  const deleteRule = useCallback(
    async (rule) => {
      if (!rule?.fine_rule_id) return;
      if (
        !window.confirm(
          `Delete fine rule "${rule.name || rule.fine_rule_id}"? This cannot be undone.`
        )
      )
        return;
      try {
        const FUNCTION_NAME = "deleteFineRule.php";
        const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;
        const schoolCode =
          SessionManager.getSchoolCode && SessionManager.getSchoolCode();
        const { data: response } = await axios.get(URL, {
          params: {
            school_code: schoolCode,
            session_id: SESSION_ID,
            fine_rule_id: rule.fine_rule_id,
          },
        });
        if (response?.api_status === "OK") {
          myToast.notify_success("Fine rule deleted");
          fetchFineRules(true);
        } else {
          myToast.notify_warning(response?.api_remark || "Delete failed");
        }
      } catch (e) {
        console.error("Error deleting fine rule:", e);
        myToast.notify_error(
          "Error deleting fine rule: " + (e?.message || "Unknown error")
        );
      }
    },
    [SESSION_ID, fetchFineRules]
  );

  /* ---------- Derived ---------- */
  const filteredRules = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return fineRules;
    return fineRules.filter((r) => {
      const haystack = [
        r.name,
        r.fine_rule_id,
        r.rule_type,
        r.description,
      ]
        .map((x) => String(x ?? "").toLowerCase())
        .join(" ");
      return haystack.includes(term);
    });
  }, [fineRules, searchTerm]);

  const stableEditHandler = useMemo(() => openEdit, [openEdit]);
  const stableDeleteHandler = useMemo(() => deleteRule, [deleteRule]);

  /* ---------- Render ---------- */
  return (
    <div
      className="content frule-page"
      style={{
        background: ui.pageBg,
        fontFamily: fontStack,
        color: ui.text,
        minHeight: "100%",
      }}
    >
      <style>{PAGE_CSS}</style>

      {/* Breadcrumbs */}
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ borderBottom: `1px solid ${ui.divider}`, paddingBottom: 6 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <a
            href="/AdminMain"
            aria-label="Admin home"
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(3, 169, 244, 0.12)",
              color: COLORS.primary_color_shade6,
              textDecoration: "none",
            }}
          >
            <FaHome />
          </a>
          <span
            className="d-inline-flex align-items-center gap-1"
            style={{ fontSize: 13, fontWeight: 700, color: ui.textSoft }}
          >
            <FaGavel style={{ fontSize: 11 }} />
            Fine Rules
          </span>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div
        className="bg_page_white_rounded box_shadow animated fadeInUp"
        style={{
          "--mMargin": "16px",
          background: "transparent",
          boxShadow: "none",
        }}
      >
        {/* Hero card */}
        <div
          style={{
            background: ui.surface,
            borderRadius: ui.radiusXl,
            border: `1px solid ${ui.border}`,
            boxShadow: ui.shadowLift,
            overflow: "hidden",
            maxWidth: 1180,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {/* Hero header */}
          <div
            style={{
              background: ui.heroGrad(COLORS),
              padding: "16px 18px",
              borderBottom: "1px solid rgba(255,255,255,0.12)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {HERO_ORB_TR}
            {HERO_ORB_BL}

            <div
              className="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center justify-content-between gap-3"
              style={{ position: "relative" }}
            >
              <div className="d-flex align-items-center gap-3 text-white flex-shrink-0 min-w-0">
                <span
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 14,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255,255,255,0.14)",
                    border: "1px solid rgba(255,255,255,0.28)",
                    backdropFilter: "blur(6px)",
                  }}
                  aria-hidden
                >
                  <FaGavel style={{ fontSize: 16 }} />
                </span>
                <div className="min-w-0">
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 17,
                      letterSpacing: "0.01em",
                    }}
                  >
                    Fine Rules
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
                    Configure rule templates used by fine structures
                  </div>
                </div>
              </div>

              <div className="d-flex flex-wrap align-items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={ChangeSession}
                  className="frule-pill"
                  title="Change session"
                  aria-label="Change session"
                  style={{
                    minWidth: 360,
                    maxWidth: "100%",
                    justifyContent: "space-between",
                    fontSize: 14,
                    padding: "8px 16px",
                    gap: 10,
                  }}
                >
                  <span
                    className="d-inline-flex align-items-center gap-2"
                    style={{ minWidth: 0 }}
                  >
                    <FaCalendarAlt aria-hidden />
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {SESSION_NAME || "Select session"}
                    </span>
                  </span>
                  <FaExchangeAlt
                    style={{ fontSize: 12, opacity: 0.9, flexShrink: 0 }}
                    aria-hidden
                  />
                </button>
                <button
                  type="button"
                  onClick={() => fetchFineRules(false)}
                  disabled={isLoadingRules || !SESSION_ID}
                  className={`frule-icon-btn ${
                    isLoadingRules ? "spinning" : ""
                  }`}
                  title="Refresh"
                  aria-label="Refresh"
                >
                  <FaSync aria-hidden />
                </button>
              </div>
            </div>
          </div>

          {/* List header */}
          <div
            style={{
              background: ui.surfaceAlt,
              padding: "12px 18px",
              borderBottom: `1px solid ${ui.divider}`,
              position: "relative",
            }}
          >
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div className="d-flex align-items-center gap-2 min-w-0">
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 800,
                    color: ui.text,
                  }}
                >
                  Rule List
                </span>
                <span
                  style={{
                    fontSize: 11,
                    color: ui.muted,
                    fontWeight: 700,
                    background: "rgba(148, 163, 184, 0.18)",
                    padding: "2px 8px",
                    borderRadius: 999,
                  }}
                >
                  {fineRules.length}{" "}
                  {fineRules.length === 1 ? "rule" : "rules"}
                </span>
                {SESSION_START_DATE && SESSION_END_DATE ? (
                  <span
                    style={{
                      fontSize: 11,
                      color: ui.muted,
                      fontWeight: 600,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <FaCalendarAlt style={{ fontSize: 10 }} aria-hidden />
                    {SESSION_START_DATE} → {SESSION_END_DATE}
                  </span>
                ) : null}
              </div>

              <div
                className="frule-search-wrap"
                style={{ background: "rgba(15, 23, 42, 0.06)", borderRadius: 999 }}
              >
                <FaSearch
                  className="frule-search-icon"
                  style={{ color: ui.muted }}
                  aria-hidden
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, type, ID…"
                  className="frule-search"
                  style={{
                    background: "transparent",
                    border: `1px solid ${ui.border}`,
                    color: ui.text,
                    minWidth: 240,
                  }}
                  aria-label="Search fine rules"
                />
                {searchTerm ? (
                  <button
                    type="button"
                    className="frule-search-clear"
                    onClick={() => setSearchTerm("")}
                    title="Clear search"
                    aria-label="Clear search"
                    style={{
                      background: "rgba(15, 23, 42, 0.18)",
                      color: ui.text,
                    }}
                  >
                    <FaTimes aria-hidden />
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {/* List body */}
          <div className="p-3">
            {!SESSION_ID ? (
              <div
                className="text-center py-5"
                style={{ color: ui.muted, fontSize: 14 }}
              >
                <div
                  className="mx-auto mb-2 d-inline-flex align-items-center justify-content-center"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 18,
                    background: "rgba(148, 163, 184, 0.18)",
                    color: ui.muted,
                  }}
                  aria-hidden
                >
                  <FaCalendarAlt style={{ fontSize: 22 }} />
                </div>
                <div
                  style={{ fontWeight: 800, color: ui.text, fontSize: 15 }}
                >
                  Select a session
                </div>
                <div style={{ fontSize: 13 }}>
                  Pick a session above to view fine rules.
                </div>
                <button
                  type="button"
                  onClick={ChangeSession}
                  className="btn lift frule-cta mt-2"
                  style={{
                    borderRadius: 999,
                    fontWeight: 700,
                    padding: "8px 16px",
                    fontSize: 13,
                  }}
                >
                  <FaCalendarAlt className="me-1" /> Choose session
                </button>
              </div>
            ) : isLoadingRules ? (
              <>
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </>
            ) : filteredRules.length === 0 ? (
              <div
                className="text-center py-5"
                style={{ color: ui.muted, fontSize: 14 }}
              >
                <div
                  className="mx-auto mb-2 d-inline-flex align-items-center justify-content-center"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 18,
                    background: "rgba(148, 163, 184, 0.18)",
                    color: ui.muted,
                  }}
                  aria-hidden
                >
                  <FaGavel style={{ fontSize: 22 }} />
                </div>
                <div
                  style={{ fontWeight: 800, color: ui.text, fontSize: 15 }}
                >
                  {searchTerm
                    ? "No rules match your search"
                    : "No fine rules found"}
                </div>
                <div style={{ fontSize: 13 }}>
                  {searchTerm
                    ? "Try a different keyword or clear the search."
                    : "Create your first fine rule from the backend, then refresh."}
                </div>
                {searchTerm ? (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    className="frule-btn-ghost mt-2"
                  >
                    Clear search
                  </button>
                ) : null}
              </div>
            ) : (
              filteredRules.map((rule, idx) => (
                <FineRuleCard
                  key={rule.fine_rule_id || idx}
                  rule={rule}
                  onEdit={stableEditHandler}
                  onDelete={stableDeleteHandler}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Session selector modal (lazy) */}
      {isOpenSessionDialog ? (
        <Suspense fallback={null}>
          <MdlSelectSession
            action={HandleCloseSessionDialog}
            action2={HandleSelectedSession}
          />
        </Suspense>
      ) : null}

      {/* Edit dialog */}
      {isEditOpen ? (
        <DialogShell
          title="Edit Fine Rule"
          subtitle={editRule.fine_rule_id ? `ID: ${editRule.fine_rule_id}` : ""}
          onClose={closeEdit}
          maxWidth={720}
          footer={
            <>
              <button
                type="button"
                className="frule-btn-ghost"
                onClick={closeEdit}
                disabled={editSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn lift frule-cta"
                onClick={saveEdit}
                disabled={
                  editSaving ||
                  !editRule.fine_rule_id ||
                  !editRule.name.trim()
                }
                style={{
                  borderRadius: 999,
                  fontWeight: 700,
                  padding: "7px 16px",
                  fontSize: 13,
                }}
              >
                {editSaving ? "Saving…" : "Save changes"}
              </button>
            </>
          }
        >
          <div className="row g-2">
            <div className="col-12 col-md-6">
              <label className="frule-modal-label">Rule name</label>
              <input
                className="form-control"
                value={editRule.name}
                onChange={(e) =>
                  setEditRule({ ...editRule, name: e.target.value })
                }
                placeholder="e.g. Late Fee"
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="frule-modal-label">Rule type</label>
              <select
                className="form-control"
                value={editRule.rule_type}
                onChange={(e) =>
                  setEditRule({ ...editRule, rule_type: e.target.value })
                }
              >
                <option value="">Select type…</option>
                <option value="flat_after">flat_after</option>
                <option value="per_day">per_day</option>
                <option value="weekly">weekly</option>
                <option value="monthly">monthly</option>
              </select>
            </div>

            <div className="col-12 col-md-4">
              <label className="frule-modal-label">Grace days</label>
              <input
                type="number"
                className="form-control"
                value={editRule.grace_days}
                onChange={(e) =>
                  setEditRule({ ...editRule, grace_days: e.target.value })
                }
                placeholder="e.g. 5"
                min="0"
                step="1"
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="frule-modal-label">
                <FaRupeeSign aria-hidden /> Base value
              </label>
              <input
                type="number"
                className="form-control"
                value={editRule.base_value}
                onChange={(e) =>
                  setEditRule({ ...editRule, base_value: e.target.value })
                }
                placeholder="e.g. 100"
                min="0"
                step="0.01"
              />
            </div>
            <div className="col-12 col-md-4">
              <div className="d-flex align-items-center justify-content-between">
                <label
                  className="frule-modal-label mb-0"
                  htmlFor="frule-max-cap-input"
                >
                  Max cap
                </label>
                <label
                  className="d-inline-flex align-items-center gap-1"
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: ui.textSoft,
                    cursor: "pointer",
                    userSelect: "none",
                    marginBottom: 4,
                  }}
                  title="Toggle unlimited (no upper bound)"
                >
                  <input
                    type="checkbox"
                    checked={noMaxCapLimit}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setNoMaxCapLimit(next);
                      if (next) {
                        setEditRule({ ...editRule, max_cap: "" });
                      }
                    }}
                    style={{ accentColor: COLORS.primary_color_shade6 }}
                  />
                  No limit
                </label>
              </div>
              <input
                id="frule-max-cap-input"
                type="number"
                className="form-control"
                value={noMaxCapLimit ? "" : editRule.max_cap}
                onChange={(e) =>
                  setEditRule({ ...editRule, max_cap: e.target.value })
                }
                placeholder={
                  noMaxCapLimit ? "No limit" : "Leave empty for no limit"
                }
                min="0"
                step="0.01"
                disabled={noMaxCapLimit}
                style={
                  noMaxCapLimit
                    ? { background: ui.surfaceAlt, color: ui.muted }
                    : undefined
                }
              />
            </div>

            <div className="col-12 col-md-6">
              <label className="frule-modal-label">Status</label>
              <select
                className="form-control"
                value={editRule.status}
                onChange={(e) =>
                  setEditRule({ ...editRule, status: e.target.value })
                }
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div className="col-12 col-md-6">
              <label className="frule-modal-label">Rule ID</label>
              <input
                className="form-control"
                value={editRule.fine_rule_id}
                disabled
                style={{ background: ui.surfaceAlt, color: ui.muted }}
              />
            </div>

            <div className="col-12">
              <label className="frule-modal-label">Description</label>
              <textarea
                className="form-control"
                rows={2}
                value={editRule.description}
                onChange={(e) =>
                  setEditRule({ ...editRule, description: e.target.value })
                }
                placeholder="Brief description shown on the rule list."
              />
            </div>

            <div className="col-12">
              <label className="frule-modal-label">
                Rule values (JSON)
              </label>
              <textarea
                className="form-control"
                rows={3}
                value={editRule.rule_values}
                onChange={(e) =>
                  setEditRule({ ...editRule, rule_values: e.target.value })
                }
                placeholder={
                  '{ "flat_after": 50, "per_day": 10, "weekly": 70, "monthly": 200 }'
                }
                style={{ fontFamily: "monospace", fontSize: 12 }}
              />
            </div>

            <div className="col-12">
              <div
                className="d-flex align-items-start gap-2"
                style={{
                  background: ui.surfaceAlt,
                  border: `1px solid ${ui.divider}`,
                  borderRadius: 10,
                  padding: "10px 12px",
                  color: ui.textSoft,
                  fontSize: 12,
                }}
              >
                <FaInfoCircle
                  style={{ color: ui.info, marginTop: 2 }}
                  aria-hidden
                />
                <div>
                  Rule name and ID are required. Leave Max Cap empty (or toggle{" "}
                  <strong>No limit</strong>) for no upper bound. Rule values is
                  optional — use it for per-bucket rates (e.g. flat_after /
                  per_day / weekly / monthly).
                </div>
              </div>
            </div>
          </div>
        </DialogShell>
      ) : null}
    </div>
  );
};

export default FineRuleSettings;
